// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Alert,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Stack,
  InputLabel,
} from '@mui/material';

// REQUEST
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

// MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// UTILS
import { validatePhoneNumber, validateName } from '../../../../utils/validate';

const SingleView = (props) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [smsContact, setSmsContact] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [edits, setEdits] = useState({});
  const { updateSmsContact } = useAddressBookRequest();

  useEffect(() => {
    const { selected } = props;
    setSmsContact(selected);
    setEdits({
      contact_first_name: selected.contact_first_name,
      contact_last_name: selected.contact_last_name,
      contact_email: selected.contact_email,
      contact_tn: selected.contact_tn,
      contact_name: selected.contact_name,
      contact_type: selected.contact_type,
    });
  }, [props.selected]);

  /*
    Handles state changes from the Form
  */
  const handleChangeEvent = (e) => {
    const target = e.target;
    let value = target.value;
    const name = target.name;

    if (name === 'email') {
      value = value.toLowerCase();
    }

    setEdits((prevEdits) => ({ ...prevEdits, [name]: value }));
  };

  /*
    Handles access level & type for read only view
  */
  const handleAccessLevel = () => {
    return (
      (props.accessLevel > '0' && props.accessType !== '0') || // if not customer user
      smsContact.contact_type === 'personal'
    ); // if customer user is editing personal address book
  };

  const errorHandler = (error) => {
    setError(error);
  };

  const handleSubmit = async () => {
    if (!edits.contact_tn) {
      return setError('Contact Number is required');
    }
    if (edits.contact_tn === smsContact.contact_tn) {
      delete edits.contact_tn;
    }
    // If number isn't only digits
    if (edits.contact_tn) {
      let validated = validatePhoneNumber(edits.contact_tn);
      if (!validated || edits.contact_tn.trim() === '') {
        return setError(
          'Incorrect contact phone format, should only contain numbers and "+", eg: +0123456789/0123456789'
        );
      }
    }
    if (edits.contact_name === smsContact.contact_name) {
      delete edits.contact_name;
    }
    if (edits.contact_name && !validateName(edits.contact_name)) {
      return setError('Display Name is required');
    }

    // If not already saving...
    setIsSaving(true);
    setError('');

    await updateSmsContact(
      smsContact.sms_contact_id,
      smsContact.contact_type,
      edits,
      errorHandler
    );
    if (!error) {
      props.callBack(true);
    }
    setIsSaving(false);
  };

  const handleButtonGroup = () => {
    if (handleAccessLevel()) {
      return (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Stack sx={{ flexDirection: 'row', gap: 2, justifyContent: 'end' }}>
              <Button color="secondary" variant="link" onClick={props.callBack}>
                {AddressBookFormattedMessages.cancelText}
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  edits.contact_name === '' ||
                  edits.contact_tn === '' ||
                  edits.contact_type === '' ||
                  isSaving
                }
                color="secondary"
              >
                {isSaving
                  ? AddressBookFormattedMessages.saving
                  : AddressBookFormattedMessages.save}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      );
    }
  };

  /*
    Handles any errors generated
  */
  const generateError = () => {
    if (error) {
      return (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        </Grid>
      );
    }
    // Return no error if none present
    return null;
  };

  return (
    <Box>
      <Box component="form">
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl component="fieldset">
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <FormLabel sx={{ minWidth: 150 }} required>
                  {AddressBookFormattedMessages.contactType}
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="contactType"
                  name="contact_type"
                  value={edits.contact_type}
                  onChange={handleChangeEvent}
                >
                  <FormControlLabel
                    value="personal"
                    control={
                      <Radio
                        color="secondary"
                        checked={edits.contact_type === 'personal'}
                        disabled={true}
                      />
                    }
                    label={AddressBookFormattedMessages.personal}
                  />
                  <FormControlLabel
                    value="global"
                    control={
                      <Radio
                        color="secondary"
                        checked={edits.contact_type === 'global'}
                        disabled={true}
                      />
                    }
                    label={AddressBookFormattedMessages.global}
                    disabled={
                      props.accessLevel > '0' && props.accessType !== '0'
                        ? false
                        : true
                    }
                  />
                </RadioGroup>
              </Stack>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }} required>
                {AddressBookFormattedMessages.displayName}
              </InputLabel>

              <TextField
                name="contact_name"
                value={edits.contact_name}
                readOnly={handleAccessLevel()}
                onChange={handleChangeEvent}
                required
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }}>
                {AddressBookFormattedMessages.emailText}
              </InputLabel>

              <TextField
                name="contact_email"
                value={edits.contact_email}
                readOnly={handleAccessLevel()}
                onChange={handleChangeEvent}
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }} required htmlFor="contact_tn">
                {AddressBookFormattedMessages.phoneText}
              </InputLabel>

              <TextField
                name="contact_tn"
                value={edits.contact_tn}
                readOnly={handleAccessLevel()}
                onChange={handleChangeEvent}
                required
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>{generateError()}</Grid>
          <Grid size={12}>{handleButtonGroup()}</Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SingleView;
